'use client';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { colors, theme } from '@startengine/design-system.theme.main';
import { useParams, useSearchParams, useRouter, usePathname } from 'next/navigation';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';

import { CustomTooltip } from '../styles';
import {
  investNowClick,
  trackEvent,
  trackSEEvent,
  ADD_TO_CART_SURFACES,
} from '../../../utils/analytics';
import CriteriaBadge from './CriteriaBadge/CriteriaBadge';
import { getCTAText } from './helpers';
import HeroImage from './HeroImage';
import {
  AccreditedInvestorsTypography,
  ClosedButtonLarge,
  CriteriaBadgeBox,
  HeadingGrid,
  HeadingPillTypography,
  HeadingTypography,
  InvestButtonLarge,
  LegalGrid,
  TestingButtonLarge,
  TypographyTagLine,
  IraOnlySubtitle,
} from './styles';
import SEContact from './SEContact';
import useIsMobile from 'hooks/useIsMobile';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useLoginModal from 'hooks/useLoginModal';
import useOfferingDaysLeft from 'hooks/useOfferingDaysLeft';
import usePotentiallyAccredited from 'hooks/usePotentiallyAccredited';
import useGoldMeetingUrl from 'hooks/useGoldMeetingUrl';
import { LEGACY_RESERVATION_LIST } from 'utils/constants/strings';
import {
  getEnv,
  hasUserSeIraAccount,
  isRetirementFundsOnly,
  getInvestmentSourceOfFundsText,
} from 'utils/helpers';
import { INVESTOR_SIGNALS_BEHAVIORAL_EVENTS } from 'utils/investorSignalsBehavioralProperties';
import { userMenu } from 'utils/links';
import { isOverAmountRaised } from 'utils/offerings/helpers';
import useGetOffering from 'hooks/useGetOffering';
import useCheckRestrictedAccess from 'hooks/useCheckRestrictedAccess';
import { canViewRestrictedOffering } from 'lib/restrictedOfferingAccess';
import useCanadianFeatureFlag from 'hooks/useCanadianFeatureFlag';
import { isGoldCampaignSlug } from 'utils/goldCampaign';
import OfferingLegalText from 'components/Offering/OfferingLegalText';

const Social = dynamic(() => import('./Social'), {
  loading: () => <Skeleton />,
  ssr: false,
});

const ShowMoreTextComponent = dynamic(() => import('components/theme/ShowMoreTextComponent'), {
  loading: () => <Skeleton />,
  ssr: false,
});

const Heading = ({ content, elevatorPitch, coverDesktopUrl, offeringDataServer }) => {
  const [getTillOfferingClose] = useOfferingDaysLeft();

  const user = useSelector((state) => state.user);
  const userAccounts = useSelector((state) => state.user.userAccounts);
  const investmentAccountStatus = useSelector((state) => state.user.investmentAccountStatus);

  const isMobile = useIsMobile();
  const isTabletOnly = useMediaQuery(theme.breakpoints.only('sm'));
  const isMediumOnly = useMediaQuery(theme.breakpoints.only('md'));
  const params = useParams();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Canadian investor flow controlled by PostHog feature flag 'canadian-investor-flow'
  // When disabled (default): Canadian QPs can invest in US-based offerings at /private
  // When enabled: Redirects to /private-canada/invest for Canadian accreditation flow
  const { isEnabled: isCanadianFeatureEnabled } = useCanadianFeatureFlag();
  const isCanadianRoute = isCanadianFeatureEnabled && pathname?.startsWith('/private-canada/');

  const { data: offeringDataClient } = useGetOffering({
    slug: params?.slug?.[0],
  });

  const offeringRestrictionType = offeringDataServer?.restrictionType;
  useEffect(() => {
    if (
      offeringRestrictionType &&
      !['PUBLIC', 'AUTHENTICATED_USERS', 'ALLOWLIST_ONLY'].includes(offeringRestrictionType)
    ) {
      console.warn(`Unknown restrictionType: ${offeringRestrictionType}`);
    }
  }, [offeringRestrictionType]);
  const isRestricted = offeringRestrictionType
    ? offeringRestrictionType !== 'PUBLIC'
    : !!offeringDataServer?.restricted;

  const { hasAccess, isSuccess } = useCheckRestrictedAccess({
    offeringId: offeringDataServer?.id,
    primaryUserId: user?.primaryUserId,
    userRoles: user?.roles,
    restrictionType: offeringRestrictionType,
    enabled: !!isRestricted && !!user?.primaryUserId,
  });

  // Check if user has a role that can always access restricted offerings
  const hasRestrictedAccessRole = canViewRestrictedOffering(user?.roles);

  useEffect(() => {
    // Only redirect if user is explicitly not signed in (not during loading)
    if (isRestricted && user && !user?.primaryUserId && user?.signedIn === false) {
      window.location.href = `https://user.startengine.com/?redirect=${window.location.href}`;
    }
  }, [isRestricted, user, user?.primaryUserId, router]);

  useEffect(() => {
    // Only redirect if the API call has completed (isSuccess: true) AND access is denied
    // AND user lacks a restricted-access role (those roles can always access restricted offerings)
    // AUTHENTICATED_USERS mode never redirects logged-in users to /explore
    if (
      isSuccess &&
      hasAccess === false &&
      isRestricted &&
      offeringRestrictionType !== 'AUTHENTICATED_USERS' &&
      user?.primaryUserId &&
      !hasRestrictedAccessRole
    ) {
      router.push('/explore');
    }
  }, [
    hasAccess,
    isRestricted,
    offeringRestrictionType,
    user?.primaryUserId,
    isSuccess,
    router,
    hasRestrictedAccessRole,
  ]);

  const offeringData = offeringDataServer || offeringDataClient;

  const isApproved = offeringData && offeringData.status === 'approved';
  const isClosed = offeringData && offeringData.status === 'closed';

  const heroContent = useMemo(() => {
    if (isApproved && isClosed) {
      return content;
    }

    return offeringData?.content;
  }, [offeringData, content, isApproved, isClosed]);

  const heroElevatorPitch = useMemo(() => {
    if (isApproved && isClosed) {
      return elevatorPitch;
    }

    return offeringData?.elevatorPitch;
  }, [offeringData, elevatorPitch, isApproved, isClosed]);

  const slug = useMemo(() => offeringData?.slug || params?.slug?.[0], [offeringData, params]);

  const isShowMoreExpanded = useMemo(
    () => searchParams.get('headingExpanded') || false,
    [searchParams],
  );

  const isOverRaised = useMemo(
    () => isOverAmountRaised(offeringData) || offeringData?.lockedOnWaitlist,
    [offeringData],
  );
  const legalText = offeringData ? <OfferingLegalText offeringData={offeringData} /> : null;
  const {
    needToLogin,
    verifiedPrimaryUserId,
    loading: partnerLoading,
  } = useSelector((state) => state.partnerVerification);
  const [renderLoginModal, setLoginModalOpen] = useLoginModal();
  const [url, setUrl] = useState('');
  const [legalExpanded, setLegalExpanded] = useState(false);
  const CTAText = useMemo(() => getCTAText(offeringData), [offeringData]);

  const getRerouteUrl = useCallback(
    (isReserved) => {
      let url = '';

      if (LEGACY_RESERVATION_LIST.includes(offeringData?.slug)) {
        url = `${process.env.NEXT_PUBLIC_ROOT_URL}/${slug}/reserve`;
      } else if (isReserved) {
        url = `${getEnv()}investment.startengine.com/${slug}/reserve`;
      } else if (window?.location?.hash === '#enforceInvestmentAccount') {
        url = `${getEnv()}investment.startengine.com/${slug}?action=enforceInvestmentAccount`;
      } else {
        url = `${getEnv()}investment.startengine.com/${slug}`;
      }

      return url;
    },
    [offeringData?.slug, slug],
  );

  const handleClick = useCallback(
    async (isReserved) => {
      // Check for Canadian investor flow - redirect to Canadian invest page
      if (isCanadianRoute) {
        window.location.href = `/private-canada/invest?offering=${slug}`;
        return;
      }

      if (investmentAccountStatus?.investmentAccountFrozen) {
        window.location.href = userMenu.portfolio;
        return;
      }

      const url = getRerouteUrl(isReserved);

      if (offeringData) {
        investNowClick({
          offeringData,
          user,
          surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE,
        });
        await trackSEEvent(
          INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow,
          offeringData?.slug,
          user,
        );
        await trackEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData);
      }

      if (!user?.signedIn || (needToLogin && !partnerLoading)) {
        setUrl(url);
        setLoginModalOpen(true);
        return;
      }

      window.location.href = `${url}${window.location.search ? `${window.location.search}` : ''}`;
    },
    [
      offeringData,
      user,
      setLoginModalOpen,
      needToLogin,
      partnerLoading,
      getRerouteUrl,
      investmentAccountStatus,
      slug,
      isCanadianRoute,
    ],
  );

  const endDays = useMemo(
    () => getTillOfferingClose(offeringData),
    [getTillOfferingClose, offeringData],
  );
  const isRegD = useMemo(
    () => offeringData?.regulationType === 'Reg D' || offeringData?.regulationType === '506c',
    [offeringData],
  );
  const isQPOnly = useMemo(
    () => offeringData?.targetAudienceAccreditation === 'QP',
    [offeringData],
  );

  const isGoldCampaign = useMemo(
    () => isGoldCampaignSlug(offeringData?.slug),
    [offeringData?.slug],
  );
  const signedIn = useIsSignedIn();
  const { isPotentiallyAccredited } = usePotentiallyAccredited({
    primaryUserId: user?.primaryUserId,
    enabled: isGoldCampaign && signedIn === true && !!user?.primaryUserId,
  });
  const isGoldAdmin = user?.roles?.includes('ADMIN') || user?.roles?.includes('SECONDARY_ADMIN');
  const showGoldMeetingCard =
    isGoldCampaign && signedIn === true && (isPotentiallyAccredited || isGoldAdmin);

  const goldMeetingUrl = useGoldMeetingUrl(showGoldMeetingCard);

  const onCTAOnClick = useCallback(async () => {
    if (offeringData && offeringData.status === 'not_submitted') return null;

    // Check for frozen account first - redirect directly to portfolio
    if (user?.investmentAccountStatus?.investmentAccountFrozen) {
      window.location.href = userMenu.portfolio;
      return null;
    }

    await handleClick(false);
    return null;
  }, [offeringData, handleClick, user?.investmentAccountStatus?.investmentAccountFrozen]);

  const renderCTAButton = useCallback(() => {
    if (!offeringData) return null;
    return (
      offeringData &&
      (offeringData.status === 'approved' ||
        offeringData.status === 'pending' ||
        offeringData.status === 'not_submitted') && (
        <>
          <InvestButtonLarge
            disabled={offeringData && offeringData.status === 'not_submitted'}
            color="primary"
            variant="contained"
            data-cy="campaign-page-hero-cta-button"
            className="campaign-page-hero-cta-button"
            onClick={onCTAOnClick}
            sx={{
              backgroundColor: isOverRaised ? colors?.SEOrange : colors?.SEDark,
              '&:hover': {
                cursor: 'pointer',
                backgroundColor: isOverRaised
                  ? `${colors?.SEOrange} !important`
                  : `${colors?.SEDark} !important`,
              },
            }}
          >
            {isOverAmountRaised(offeringData) || offeringData?.lockedOnWaitlist
              ? 'Join the Waitlist'
              : CTAText}
          </InvestButtonLarge>
          {isRetirementFundsOnly(offeringData) && !hasUserSeIraAccount(userAccounts) && (
            <IraOnlySubtitle color="primary">You can open an IRA with StartEngine</IraOnlySubtitle>
          )}
        </>
      )
    );
  }, [offeringData, onCTAOnClick, isOverRaised, CTAText, userAccounts]);

  const renderStatusBadges = () => {
    if (!offeringData) return null;
    return (
      <Grid item mb={0.5} xs={12}>
        {isOverRaised && (
          <div
            data-cy="campaign-page-hero-waitlist-badge"
            style={{
              backgroundColor: '#FFEAD6',
              borderRadius: '8px',
              display: 'inline-block',
              marginRight: '8px',
              marginBottom: '8px',
            }}
          >
            <HeadingPillTypography color={colors?.SEOrange}>
              WAITLIST
              <CustomTooltip
                color={colors?.SEOrange}
                content="This campaign has exceeded its maximum funding goal. You can still invest, but you will be added to a waitlist."
              />
            </HeadingPillTypography>
          </div>
        )}
        {endDays && !(isMobile && isRegD) && (
          <div
            style={{
              backgroundColor: '#FFE0E9',
              borderRadius: '8px',
              display: 'inline-block',
              marginRight: '8px',
              marginBottom: '8px',
            }}
          >
            <HeadingPillTypography color={theme.palette.common.imgText}>
              {endDays.toUpperCase()}
              <CustomTooltip
                color={theme.palette.common.imgText}
                content="Closing date is based on the most recent SEC filing and may be subject to change.
                This campaign may be extended further if an amendment is filed."
              />
            </HeadingPillTypography>
          </div>
        )}
        {isRegD && !isQPOnly && (
          <Box
            sx={{
              backgroundColor: 'rgba(24, 92, 101, 0.1)',
              borderRadius: '8px',
              display: 'inline-block',
              marginRight: '8px',
              marginBottom: '8px',
            }}
          >
            <AccreditedInvestorsTypography>ACCREDITED INVESTORS ONLY</AccreditedInvestorsTypography>
          </Box>
        )}
        {isQPOnly && (
          <Box
            sx={{
              backgroundColor: 'rgba(24, 92, 101, 0.1)',
              borderRadius: '8px',
              display: 'inline-block',
              marginRight: '8px',
              marginBottom: '8px',
            }}
          >
            <AccreditedInvestorsTypography>QUALIFIED PURCHASERS ONLY</AccreditedInvestorsTypography>
          </Box>
        )}
        {isRetirementFundsOnly(offeringData) && (
          <Box
            sx={{
              backgroundColor: 'rgba(24, 92, 101, 0.1)',
              borderRadius: '8px',
              display: 'inline-block',
              marginRight: '8px',
              marginBottom: '8px',
            }}
          >
            <AccreditedInvestorsTypography>
              {getInvestmentSourceOfFundsText(offeringData.investmentSourceOfFunds)}
            </AccreditedInvestorsTypography>
          </Box>
        )}
        {offeringData?.internationalInvestorsAllowed === false && (
          <Box
            sx={{
              backgroundColor: 'rgba(24, 92, 101, 0.1)',
              borderRadius: '8px',
              display: 'inline-block',
              marginRight: '8px',
              marginBottom: '8px',
            }}
          >
            <AccreditedInvestorsTypography>US INVESTORS ONLY</AccreditedInvestorsTypography>
          </Box>
        )}
      </Grid>
    );
  };

  const renderContent = () => {
    return (
      <>
        <Grid item xs={12}>
          <HeadingTypography component="h1" color="primary">
            {offeringData?.name ? `Invest in ${offeringData?.name}` : <Skeleton />}
          </HeadingTypography>
        </Grid>
        <Grid item xs={12}>
          <TypographyTagLine color="primary" variant="h3">
            {heroContent}
          </TypographyTagLine>
        </Grid>
        <Grid item xs={12} mt={1}>
          <ShowMoreTextComponent
            content={heroElevatorPitch || ''}
            lines={3}
            expanded={isShowMoreExpanded}
            isHtml
          />
        </Grid>
      </>
    );
  };

  const renderCTASection = () => {
    return (
      <Grid item sx={{ width: '100%' }} mt={2}>
        {renderCTAButton()}
        {offeringData && offeringData.status === 'closed' && (
          <ClosedButtonLarge>Offering Closed</ClosedButtonLarge>
        )}
        {offeringData && offeringData.status === 'testing' && (
          <TestingButtonLarge onClick={() => handleClick(true)}>Reserve Now</TestingButtonLarge>
        )}
        <LegalGrid item xs={12} sm={6}>
          {isMobile ? (
            <>
              <Collapse in={legalExpanded} collapsedSize={0}>
                <Box sx={{ pb: 1 }}>{legalText || <Skeleton />}</Box>
              </Collapse>
              <Box
                component="button"
                onClick={() => setLegalExpanded((prev) => !prev)}
                sx={{
                  background: 'none',
                  border: 'none',
                  color: colors.SEDark,
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '4px 0',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                data-cy="legal-text-toggle"
              >
                {legalExpanded ? 'Hide disclaimer' : 'View SEC disclaimer'}
              </Box>
            </>
          ) : (
            legalText || <Skeleton />
          )}
        </LegalGrid>
        {(isMobile || isMediumOnly) && (
          <CriteriaBadgeBox
            item
            xs={12}
            container
            alignItems="center"
            justifyContent={isMediumOnly ? 'flex-start' : 'center'}
            my={2}
          >
            <CriteriaBadge offering={offeringData} isMobile />
          </CriteriaBadgeBox>
        )}
        {isMobile &&
          (isGoldCampaign
            ? showGoldMeetingCard &&
              goldMeetingUrl && (
                <SEContact mt="15px" mb="20px" calendarUrlOverride={goldMeetingUrl} />
              )
            : offeringData?.regulationType === 'Reg D' && <SEContact mt="15px" mb="20px" />)}
      </Grid>
    );
  };

  return (
    <HeadingGrid container alignItems="center" justifyContent="space-between">
      {isMobile && <Social offeringClosed={offeringData && offeringData.status === 'closed'} />}
      {isMobile && (
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            marginTop: {
              xs: offeringData && offeringData.status === 'closed' ? 3 : 0,
              sm: 0,
              lg: 3,
            },
          }}
          ml="auto"
          mr="auto"
          container
          justifyContent="center"
          position="relative"
          mb={4}
        >
          <HeroImage coverDesktopUrl={coverDesktopUrl} />
        </Grid>
      )}
      <Grid item xs={12} md={6} mt={isTabletOnly ? 7 : '0'} container>
        {renderStatusBadges()}
        {renderContent()}
        {renderCTASection()}
      </Grid>
      {!isMobile && (
        <Grid item xs={12} md={6} container justifyContent="center" position="relative">
          <HeroImage coverDesktopUrl={coverDesktopUrl} />
        </Grid>
      )}
      {renderLoginModal({
        offering: offeringData,
        verifiedPrimaryUserId,
        onClose: (user) => {
          if (user?.event === 'signup') {
            const search = window.location.search ? `${window.location.search}` : '';
            window.location.href = `${url}${search}`;
          }
        },
        onSuccessfulLogin: () => {
          // PD-34901 — login-then-invest flow; `user.id` is now populated
          // post-login, so this fires `add_to_cart` correctly.
          investNowClick({
            offeringData,
            user,
            surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE,
          });
          const search = window.location.search ? `${window.location.search}` : '';
          window.location.href = `${url}${search}`;
        },
      })}
    </HeadingGrid>
  );
};
export default Heading;
